/**
 * What the native app asks of the page (`window.revisa`): to show another page in place (a page it keeps warm for the
 * next it opens), to say where the page is and how far it's scrolled (kept while it's let go of), and to come back to
 * that; and, each time, to say when the page is drawn (its requests answered), so it slides in whole. It tells the
 * app where it is (`place`: the path, its space, whether you're signed in) as that changes, and who you are (`me`: your
 * name and picture, your spaces, and the sections' and your account's names in your language), and whether something's
 * open over it (`modal`: the omnibar, a dialog). To an app whose back closes what's open in the page first (Android's:
 * "back"), whether anything is (`closable`), and it closes it (`back`).
 */
import { effect } from "@preact/signals";
import { h } from "preact";
import { API, inFlight, api as server, session } from "../api/client.ts";
import type { AppSession } from "../api/types.gen.ts";
import { locale, t } from "../i18n/index.ts";
import { NAV_FILLED_ICONS, NAV_ICONS } from "../ui/icons.tsx";
import { act } from "./actions.ts";
import { hexOf } from "./cssColor.ts";
import { type Glyph, glyphInside, glyphOf } from "./glyphs.ts";
import { loadCatalog } from "./iconCatalog.ts";
import { post, provides, REVEALED, scaleText, setKeyboard, shell, unpark } from "./native.ts";
import { watchSelects } from "./nativeMenu.ts";
import { popoverApi, watchPopovers } from "./nativePopover.ts";
import { omnibarOpen } from "./omnibar.ts";
import { herePath, location, navigate, spacePath } from "./router.ts";
import { me, mySpaces } from "./state.ts";
import { applyTheme, asChoice } from "./theme.ts";

/** Waits this long at most for a page's requests (a slow one doesn't hold the app's transition). */
const PATIENCE_MS = 3000;

/** A page out of sight gets no frames: it's taken as drawn after this long. */
const UNSEEN_MS = 100;

/** Two frames from now: what's been rendered is on screen (or soon, out of sight). */
const frames = () =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, UNSEEN_MS);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        clearTimeout(timer);
        resolve();
      }),
    );
  });

/** Resolves once no request is on its way (two frames after the last), or at the latest after `PATIENCE_MS`. */
async function settled(): Promise<void> {
  const until = Date.now() + PATIENCE_MS;
  for (;;) {
    await frames();
    if (inFlight.value === 0 || Date.now() > until) {
      return;
    }
    await new Promise<void>((resolve) => {
      const timer = setTimeout(done, Math.max(0, until - Date.now()));
      const stop = effect(() => {
        if (inFlight.value === 0) {
          done();
        }
      });
      function done() {
        clearTimeout(timer);
        // Within the effect's first run `stop` isn't there yet: let go of it on the next turn.
        queueMicrotask(() => stop());
        resolve();
      }
    });
  }
}

/** One of a dialog's answers, as a native sheet's bar draws it (its icon, if it has one: else the system's). */
interface Answer {
  label: string;
  danger: boolean;
  disabled: boolean;
  busy: boolean;
  /** What the bar shows of it: its icon (else ✓), its words, or both. */
  shows: "icon" | "text" | "both";
  glyph: Glyph | null;
}

/** Answers that only say yes (no more than the system's ✓). */
const PLAIN = ["common.done"] as const;

/** What a bar shows of answer `b`: as it says; else (auto) ✓ when it only says yes ("Done"), its words otherwise. */
function showsOf(b: HTMLButtonElement, label: string, danger: boolean): Answer["shows"] {
  const said = b.dataset["answerShows"];
  if (said === "icon" || said === "text" || said === "both") {
    return said;
  }
  return !danger && PLAIN.some((k) => t(k) === label) ? "icon" : "text";
}

/** A dialog's title and answers (cancelling, confirming, a step back), for a native sheet's bar to draw instead. */
interface SheetBar {
  title: string;
  cancel: Answer | null;
  confirm: Answer | null;
  back: Answer | null;
}

interface Answers {
  cancel: HTMLButtonElement | null;
  confirm: HTMLButtonElement | null;
  /** A step back (a wizard's). */
  back: HTMLButtonElement | null;
}

type Which = keyof Answers;

/**
 * The open dialog's answers: its buttons said to be (`answer`), the last of each; else its last row of buttons, when
 * it's one to cancel and one to confirm (primary or danger). None: its buttons stay its own.
 */
function answers(open: Element): Answers | null {
  const said = (which: Which) =>
    [...open.querySelectorAll<HTMLButtonElement>(`button[data-answer=${which}]`)].at(-1) ?? null;
  const cancel = said("cancel");
  const confirm = said("confirm");
  const back = said("back");
  if (cancel || confirm || back) {
    return { cancel, confirm, back };
  }
  const row = [...open.querySelectorAll("[data-dialog-actions]")].at(-1);
  const buttons = row ? [...row.querySelectorAll<HTMLButtonElement>(":scope > button")] : [];
  if (!row || buttons.length !== 2 || row.childElementCount !== 2) {
    return null;
  }
  const [no, yes] = buttons as [HTMLButtonElement, HTMLButtonElement];
  const kind = (b: HTMLButtonElement) => b.dataset["variant"];
  return kind(no) === "secondary" && (kind(yes) === "primary" || kind(yes) === "danger")
    ? { cancel: no, confirm: yes, back: null }
    : null;
}

const answerOf = (b: HTMLButtonElement | null): Answer | null => {
  if (!b) {
    return null;
  }
  const busy = b.getAttribute("aria-busy") === "true";
  const label = b.textContent.trim();
  const danger = b.dataset["variant"] === "danger";
  return {
    label,
    danger,
    shows: showsOf(b, label, danger),
    disabled: b.disabled,
    busy,
    // Its own icon (not the spinner it shows while busy).
    glyph: busy ? null : glyphInside(b),
  };
};

/** The open dialog's bar, as a native sheet draws it (its title and answers moved there). */
function sheetBar(open: Element): SheetBar {
  const both = answers(open);
  return {
    title: open.querySelector("[data-dialog-title]")?.textContent.trim() ?? "",
    cancel: answerOf(both?.cancel ?? null),
    back: answerOf(both?.back ?? null),
    confirm: answerOf(both?.confirm ?? null),
  };
}

/** The page's own confirming button (a form's Save: `answer="confirm"`, in none of its dialogs), the last. */
const pageConfirm = (): HTMLButtonElement | null =>
  [...document.querySelectorAll<HTMLButtonElement>("button[data-answer=confirm]")]
    .filter((b) => !b.closest("dialog"))
    .at(-1) ?? null;

/**
 * The answers of `open` (a dialog in the sheet; none: none) marked as in the sheet's bar, and no others: changing only
 * what's to change (the marks being changes too, told to what watches the dialog).
 */
function take(open: Element | null): void {
  const both = open ? answers(open) : null;
  const now = new Set([both?.cancel, both?.confirm, both?.back].filter((b) => b !== null && b !== undefined));
  for (const b of document.querySelectorAll("[data-sheet-taken]")) {
    if (!now.has(b as HTMLButtonElement)) {
      b.removeAttribute("data-sheet-taken");
    }
  }
  for (const b of now) {
    if (!b.hasAttribute("data-sheet-taken")) {
      b.setAttribute("data-sheet-taken", "");
    }
  }
}

/** Told again as the dialog changes (its confirm button enabled, busy), while it's in a sheet. */
let watching: MutationObserver | null = null;

/** Where the page is, and how far it's scrolled. */
interface Kept {
  path: string;
  scroll: number;
}

/** Serves the app what it asks of the page, and tells it once the first page is drawn. */
/** How the caret moves for an arrow (Selection.modify's: which way, how far). */
const CARET: Readonly<Record<string, ["backward" | "forward", "character" | "line"]>> = {
  ArrowLeft: ["backward", "character"],
  ArrowRight: ["forward", "character"],
  ArrowUp: ["backward", "line"],
  ArrowDown: ["forward", "line"],
};

/** `key` pressed (with Shift: `shift`) where the focus is: its handlers first; an arrow none took moves the caret. */
function pressKey(key: string, shift: boolean): void {
  const at = document.activeElement ?? document.body;
  const init = { key, code: key, shiftKey: shift, bubbles: true, cancelable: true, composed: true };
  const taken = !at.dispatchEvent(new KeyboardEvent("keydown", init));
  at.dispatchEvent(new KeyboardEvent("keyup", init));
  const caret = CARET[key];
  if (taken || !caret) {
    return;
  }
  if (at instanceof HTMLInputElement || at instanceof HTMLTextAreaElement) {
    // A field's caret: along its text (a line up or down: its start or end).
    const end = at.value.length;
    const from = at.selectionDirection === "backward" ? (at.selectionStart ?? 0) : (at.selectionEnd ?? 0);
    const step = caret[1] === "line" ? end : 1;
    const to = Math.min(Math.max(from + (caret[0] === "forward" ? step : -step), 0), end);
    if (shift) {
      const anchor = at.selectionDirection === "backward" ? (at.selectionEnd ?? 0) : (at.selectionStart ?? 0);
      at.setSelectionRange(Math.min(anchor, to), Math.max(anchor, to), to < anchor ? "backward" : "forward");
    } else {
      at.setSelectionRange(to, to);
    }
    return;
  }
  globalThis.getSelection()?.modify(shift ? "extend" : "move", caret[0], caret[1]);
}

export function serveShell(): void {
  if (!shell()) {
    return;
  }
  const api = {
    // A popover on the app's glass (nativePopover.ts).
    ...popoverApi,
    /** Shows `path` here instead (a page kept warm, made the next one): resolves once it's drawn. */
    visit: async (path: string): Promise<string> => {
      navigate(path, true);
      unpark();
      globalThis.scrollTo(0, 0);
      await settled();
      return herePath();
    },
    /** Shown again by the app (a fragment: the sidebar's drawer opened, its column back): its logo waves. */
    revealed: (): void => {
      dispatchEvent(new Event(REVEALED));
    },
    /** Where the page the app shows is (`path`), followed by a fragment (the sidebar marking its section), unmoved. */
    follow: (path: string): void => {
      navigate(path, true);
    },
    /** Shows the theme `theme` ("system", "light", "dark"), true black or not: chosen in another of the app's pages. */
    theme: (theme: string, trueBlack: boolean): void => {
      // Shown here as it is (what's kept is shared: already the new choice).
      applyTheme({ theme: asChoice(theme), trueBlack });
    },
    /**
     * The open dialog into the app's own sheet, in turn: `prepare`, the page without it (nor its dimming) for the app
     * to picture, once drawn (its content's height, for the sheet's); `on`, only it, filling the view (the rest
     * hidden: the page behind is the app's picture of it); `off`, the page whole again.
     */
    sheet: async (step: "prepare" | "on" | "off"): Promise<{ height: number; bar: SheetBar | null }> => {
      const root = document.documentElement;
      const open = document.querySelector("dialog:modal");
      root.toggleAttribute("data-sheet-out", step === "prepare");
      root.toggleAttribute("data-sheet", step === "on");
      // Its answers in the sheet's bar (their own row hidden), and told again as they change.
      take(step === "on" ? open : null);
      watching?.disconnect();
      watching = null;
      if (step === "prepare") {
        await frames();
      }
      if (step !== "on" || !open) {
        return { height: 0, bar: null };
      }
      let told = JSON.stringify(sheetBar(open));
      watching = new MutationObserver(() => {
        // Its answers as they are now (a wizard's next step's, its last step's gone).
        take(open);
        const now = JSON.stringify(sheetBar(open));
        if (now !== told) {
          told = now;
          post("sheetBar", JSON.parse(now) as Record<string, unknown>).catch(() => undefined);
        }
      });
      watching.observe(open, { subtree: true, childList: true, characterData: true, attributes: true });
      // Its content, as tall as it takes (its title and answers in the sheet's bar, not in it).
      return { height: (open.firstElementChild ?? open).scrollHeight, bar: sheetBar(open) };
    },
    /**
     * The open dialog's field to type into at once (`autofocus`, as the dialog says) focused, in the sheet: moved into
     * it, the page lost its focus. None said: none (the keyboard not up before you're there).
     */
    focusDialog: (): void => {
      // Where it is: not scrolled to, the sheet jumping as it comes.
      document.querySelector<HTMLElement>("dialog:modal [autofocus]")?.focus({ preventScroll: true });
    },
    /** One of the open dialog's answers chosen in the sheet's bar. */
    answer: (which: Which): void => {
      const open = document.querySelector("dialog:modal");
      const both = open ? answers(open) : null;
      both?.[which]?.click();
    },
    /** The page's own confirming button (a form's Save) pressed, from the app's bar. */
    confirmPage: (): void => {
      pageConfirm()?.click();
    },
    /** The open dialog closed (its sheet swiped away). */
    dismiss: (): void => {
      document.querySelector<HTMLDialogElement>("dialog:modal")?.close();
    },
    /** Back (the system's): what's open in the page closed, as Escape would (a popover, else a dialog); whether it was. */
    back: (): boolean => closeOpen(),
    /** The system's text size changed: the page's text (and what's sized with it) `scale` times its usual size. */
    /**
     * A key pressed on the app's own row over its keyboard (`key`: "ArrowUp", "Tab"…; with Shift or not), as the
     * focused element hears one: what it does with it, or (an arrow nothing took) the caret moved, as the key would.
     */
    key: (key: string, shift: boolean): void => {
      pressKey(key, shift);
    },
    /** The app's keyboard covers `px` of the page now (0: it's down). */
    keyboard: (px: number): void => {
      setKeyboard(px);
    },
    textScale: (scale: number): void => {
      scaleText(scale);
    },
    /** Opens the omnibar (the app's search). */
    search: (): void => {
      omnibarOpen.value = true;
    },
    /** Its header's action `key` was used (`entry`: of its menu): false, there's none now. */
    act: (key: string, entry?: string | null): boolean => act(key, entry),
    /** Where it is, to come back to (the app lets the page go meanwhile). */
    keep: (): Kept => ({ path: herePath(), scroll: globalThis.scrollY }),
    /** Back where it was: resolves once it's drawn there. */
    restore: async (k: Kept): Promise<void> => {
      navigate(k.path, true);
      await settled();
      globalThis.scrollTo(0, k.scroll);
    },
  };
  (globalThis as { revisa?: typeof api }).revisa = api;
  watchPopovers();
  watchSelects();
  // The entities' icons, loaded now: the app's menus draw them (a header's "New"), read as the header's told.
  loadCatalog().catch(() => undefined);
  // Where the page is, and whether you're signed in (null: not known yet), each time it changes: the app's tabs go to
  // that space's sections, and back to signing in when you're out.
  effect(() => {
    const path = `${location.value.pathname}${location.value.search}`;
    const signedIn = session.value === undefined ? null : session.value !== null;
    post("place", { path, space: spacePath(location.value.pathname)?.space ?? null, signedIn }).catch(() => undefined);
  });
  // Who you are, each time that (or the language) changes: the app's tabs, sidebar and drawer show it.
  effect(() => {
    const you = me.value;
    if (!you) {
      return;
    }
    post("me", {
      name: you.name,
      // The page's language (yours, else the device's nearest): the app's own words in it too.
      lang: locale.value,
      avatar: you.avatar?.url ?? null,
      account: t("account.title"),
      search: t("nav.search"),
      // Shorter, for a tab bar (the rest as they are).
      short: { triage: t("nav.short.triage"), queue: t("nav.short.queue") },
      sections: {
        triage: t("nav.triage"),
        queue: t("nav.queue"),
        conversation: t("nav.conversation"),
        reports: t("nav.reports"),
        items: t("nav.items"),
        rules: t("nav.rules"),
        history: t("nav.history"),
        bookmarks: t("nav.bookmarks"),
        trash: t("nav.trash"),
        settings: t("nav.settings"),
      },
      spaces: mySpaces().map((s) => ({ id: s.space, name: s.name, org: s.org.name })),
      // The sections' icons, drawn by the app as ours (and filled, where there's a filled one: a selected tab).
      icons: Object.fromEntries(
        (Object.keys(NAV_ICONS) as (keyof typeof NAV_ICONS)[]).map((k) => {
          const filled = NAV_FILLED_ICONS[k];
          return [
            k,
            {
              outline: glyphOf(h(NAV_ICONS[k], { size: 24 })),
              filled: filled ? glyphOf(h(filled, { size: 24 })) : null,
            },
          ];
        }),
      ),
    }).catch(() => undefined);
  });
  // The app's own parts (its share extension…) have a session of their own: started here, signed in, when the app
  // asks for one for you (it has none, or another's, or an old one), and handed to it (it keeps it out of the page's
  // reach). Asked once per you: an offline start, failed, is asked again the next time.
  let handed: string | null = null;
  effect(() => {
    const user = me.value?.id ?? null;
    if (!(user && user !== handed && provides("session"))) {
      return;
    }
    handed = user;
    handSession(user).catch(() => {
      handed = null;
    });
  });
  // Something over the page (the omnibar, a dialog: a modal <dialog>), each time that changes: the app keeps the page
  // still under it (its own scrolling off; what's open scrolls on its own).
  // A dialog dims the page: the app's bars too, in the page's dimming colour. One at the page's top (the omnibar)
  // has the title small, out of its way.
  let over = false;
  new MutationObserver(() => {
    const open = document.querySelector("dialog:modal");
    if ((open !== null) !== over) {
      over = open !== null;
      const scrim = getComputedStyle(document.documentElement).getPropertyValue("--color-scrim").trim();
      const top = open?.matches("[data-omnibar]") ?? false;
      post("modal", {
        open: over,
        top,
        // A dialog (not the omnibar, at the page's top): in the app's own sheet.
        sheet: over && !top,
        scrim: scrim ? hexOf(scrim, true) : null,
      }).catch(() => undefined);
    }
  }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ["open"] });
  // Its own confirming button (a form's Save), in the app's bar: told as it comes, goes and changes (enabled, busy),
  // once a frame at most.
  if (provides("title")) {
    let told = "null";
    let asked = false;
    const tell = () => {
      asked = false;
      const b = pageConfirm();
      const now = JSON.stringify(b ? answerOf(b) : null);
      if (now !== told) {
        told = now;
        post("pageAnswer", { answer: JSON.parse(now) as unknown }).catch(() => undefined);
      }
    };
    new MutationObserver(() => {
      if (!asked) {
        asked = true;
        requestAnimationFrame(tell);
      }
    }).observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["disabled", "aria-busy", "data-answer"],
    });
  }
  if (provides("back")) {
    watchClosable();
  }
  settled()
    .then(() => post("painted", { path: herePath() }))
    .catch(() => undefined);
}

/** What back closes, open now: the last popover (a menu, a picker), else a dialog (the omnibar too). */
const OPEN = ":popover-open, dialog:modal";

/** The last of what's open closed, as Escape would (a dialog asked to: one that won't, as unsaved, stays); whether it was. */
function closeOpen(): boolean {
  const open = [...document.querySelectorAll<HTMLElement>(OPEN)];
  const last = open.findLast((e) => e.matches(":popover-open")) ?? open.at(-1);
  if (!last) {
    return false;
  }
  if (last instanceof HTMLDialogElement && last.matches(":modal")) {
    if (typeof last.requestClose === "function") {
      last.requestClose();
    } else {
      last.close();
    }
  } else {
    last.hidePopover();
  }
  return true;
}

/** Tells the app whether anything back closes is open, as that changes (a popover toggled, a dialog opened or closed). */
function watchClosable(): void {
  let said = false;
  const check = () => {
    const now = document.querySelector(OPEN) !== null;
    if (now !== said) {
      said = now;
      post("closable", { on: now }).catch(() => undefined);
    }
  };
  // A popover's toggling doesn't bubble: heard on its way down.
  document.addEventListener("toggle", () => queueMicrotask(check), true);
  new MutationObserver(check).observe(document.body, { subtree: true, attributes: true, attributeFilter: ["open"] });
}

/** A session of the app's own for `user`, if it wants one: started, and handed to it (with where the API and the web app are). */
async function handSession(user: string): Promise<void> {
  if ((await post("needsSession", { user })) !== true) {
    return;
  }
  const s = await server.post<AppSession>("/auth/app-session");
  await post("session", { user: s.user, token: s.refresh_token, api: API, web: globalThis.location.origin });
}
